import React from 'react';
import styled, { CSSObject } from 'styled-components';
import {
  spacer8,
  spacer16,
  spacer24,
  spacer32,
  screenMdMin,
  display1FontSizeXSmall,
  heading2FontSize,
  white,
  black,
  spacer48,
  spacer40,
  screenSmMin,
  screenXsMax,
} from '@spotify-internal/encore-foundation';
import { Type } from '@spotify-internal/encore-web';
import { hexToRgba } from '@mrkt/features/color-conversion';
import { ModuleType } from '../index';
import { Box, Link, Supertext } from '../../ui';
import { formatText, youtubeIdRegex } from '../../utils';
import { VideoComponent } from '../../ui/Video';
import { ShareButtonsComponent } from '../../ui/ShareButtons';

const Wrapper = styled(Box)`
  display: flex;
  flex-flow: row-reverse wrap;
  justify-content: space-between;
  height: auto;
  background: ${black};
  & {
    padding: ${spacer48} ${spacer40};
  }

  @media (min-width: ${screenSmMin}) and (max-width: ${screenMdMin}) {
    ${(Supertext as unknown) as CSSObject} {
      padding-top: ${spacer32};
    }
  }

  @media (max-width: ${screenXsMax}) {
    padding-left: ${spacer16};
    padding-right: ${spacer16};
    p {
      padding-top: ${spacer16};
    }
  }

  @media (max-width: ${screenMdMin}) {
    flex-direction: column;
    justify-content: flex-start;
    padding-bottom: ${spacer32};
  }
`;

const InnerText = styled.div`
  padding-left: 0px;
  width: 30%;
  display: flex;
  flex-direction: column;
  justify-content: space-evenly;

  @media (max-width: ${screenMdMin}) {
    justify-content: flex-start;
    width: 100%;
    margin: 0px;
  }
`;

const VideoWrapper = styled.div`
  padding-left: 0px;
  width: 65%;

  @media (max-width: ${screenMdMin}) {
    padding-bottom: ${spacer8};
    width: 100%;
    margin: 0px;
  }
`;

// TODO Remove hardcoding of font-size - there is no 56 in Encore, it is Frodor specific
const Headline = styled(Type.h1).attrs({
  variant: Type.heading1,
  weight: Type.bold,
})`
  padding: ${spacer16} 0;
  font-size: ${display1FontSizeXSmall};
  max-width: 684px;

  @media (max-width: ${screenMdMin}) {
    text-align: left;
    font-size: ${heading2FontSize};
    padding: 0;
  }
`;

const Subtext = styled(Type.h4).attrs({
  variant: Type.heading4,
})`
  padding-bottom: ${spacer24};
`;

const AllVideosLink = styled(Link)`
  color: ${white};
  text-decoration: underline;

  &:hover:not(:focus):not(:disabled) {
    color: ${hexToRgba(white, 0.75)};
  }
`;

export type VideoModuleType = ModuleType & {
  headline: string;
  subtext?: string;
  supertext?: string;
  videoUrl: string;
};

const TRACK_CAT = 'module-video';

export const VideoModule: React.FC<VideoModuleType> = ({
  headline = '',
  subtext = '',
  supertext = '',
  videoUrl = '',
}) => {
  return (
    <Wrapper>
      <VideoWrapper>
        {videoUrl.match(youtubeIdRegex) && <VideoComponent url={videoUrl} />}
      </VideoWrapper>
      <InnerText>
        <div>
          {supertext && <Supertext>{supertext}</Supertext>}
          {headline && (
            <Headline
              data-testid="video-module-headline"
              dangerouslySetInnerHTML={{ __html: formatText(headline) }}
            />
          )}
          {subtext && (
            <Subtext
              dangerouslySetInnerHTML={{ __html: formatText(subtext) }}
            />
          )}
        </div>
        <div>
          <ShareButtonsComponent />
          <AllVideosLink href="/videos" track={[TRACK_CAT, 'All Videos Link']}>
            Back to videos
          </AllVideosLink>
        </div>
      </InnerText>
    </Wrapper>
  );
};
